import * as moment from "moment-timezone";
import { incidentDisplayDateKeyDDMMYYYY } from "./payrollUtils.ts";
import { resolveIncidentRealChecks } from "./incidentDisplayTimes.ts";

/** Incidencia solo para flujo de nómina / revisión del líder; no listar en tablas operativas. */
export function isPendingLeaderReviewIncident(incident: any): boolean {
  const row = incident as any;
  if (row.isPendingLeaderReview === true) {
    return true;
  }
  const reason = String(row.reason || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
  if (reason.includes("pendiente de revision del lider")) {
    return true;
  }
  if (
    typeof row.id === "string" &&
    row.id.startsWith("pending-review-")
  ) {
    return true;
  }
  if (
    row.incidentId &&
    row.finalStatus == null &&
    !row.authorizeBy &&
    row.type === "Aceptar"
  ) {
    return true;
  }
  return false;
}

export function excludePendingLeaderReviewIncidents<T>(incidents: T[]): T[] {
  return incidents.filter(
    (incident) => !isPendingLeaderReviewIncident(incident)
  );
}

/** RH/jefe ya atendió la incidencia (aceptar/rechazar con autorización). */
export function isResolvedOperationalIncident(incident: any): boolean {
  if (isPendingLeaderReviewIncident(incident)) {
    return false;
  }
  return incident?.finalStatus === true || Boolean(incident?.authorizeBy);
}


/** Normaliza fechas a YYYY-MM-DD. */
function toOperationalDayKey(raw: unknown): string {
  if (raw == null || raw === "") {
    return "";
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      return trimmed.slice(0, 10);
    }
  }
  const parsed = moment(raw as any);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

/** Oculta chequeos sin horario que ya tienen «No programado» resuelto en BD. */
export function filterOperationalPendingNoProgramados<T extends {
  partner?: { id?: number };
  partnerId?: number;
  checkIn?: { id?: number; checkDate?: string };
  checkOut?: { checkDate?: string };
  operationDate?: string;
  exactDate?: string;
}>(
  noProgramados: T[],
  savedIncidents: any[]
): T[] {
  const resolvedKeys = new Set<string>();
  const resolvedPartnerDays = new Set<string>();
  for (const saved of savedIncidents || []) {
    if (String(saved?.status || "") !== "No programado") {
      continue;
    }
    if (!isResolvedOperationalIncident(saved)) {
      continue;
    }
    const partnerId = Number(
      saved?.schedule?.partner?.id ?? saved?.partner?.id
    );
    const checkInId = Number(
      saved?.schedule?.assistanceCheck?.id ??
        saved?.schedule?.checkIn?.id ??
        saved?.checkIn?.id
    );
    if (partnerId && checkInId) {
      resolvedKeys.add(`${partnerId}-${checkInId}`);
    }
    const day = toOperationalDayKey(
      saved?.schedule?.exactDate ??
        saved?.checkIn?.checkDate ??
        saved?.schedule?.assistanceCheck?.checkDate
    );
    if (partnerId && day) {
      resolvedPartnerDays.add(`${partnerId}|${day}`);
    }
  }

  const seen = new Set<string>();
  return (noProgramados || []).filter((item) => {
    const partnerId = Number(item?.partner?.id ?? item?.partnerId);
    const checkInId = Number(item?.checkIn?.id);
    if (!partnerId || !checkInId) {
      return false;
    }
    const key = `${partnerId}-${checkInId}`;
    if (resolvedKeys.has(key)) {
      return false;
    }
    const day = toOperationalDayKey(
      item?.checkIn?.checkDate ?? item?.operationDate ?? item?.exactDate
    );
    if (day && resolvedPartnerDays.has(`${partnerId}|${day}`)) {
      return false;
    }
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function filterOperationalFalseNoCheckoutIncidents<T extends {
  status?: string;
  checkIn?: { id?: number };
  checkOut?: { checkTime?: string };
  schedule?: {
    partner?: { id?: number };
    exactDate?: string;
    assistanceCheckout?: { checkTime?: string };
    checkOut?: { checkTime?: string };
  };
}>(incidents: T[]): T[] {
  const seenPartnerDay = new Set<string>();
  return (incidents || []).filter((incident) => {
    const status = String(incident?.status || "");
    const resolvedChecks = resolveIncidentRealChecks(incident);
    const checkOutTime =
      incident?.checkOut?.checkTime ??
      incident?.schedule?.assistanceCheckout?.checkTime ??
      incident?.schedule?.checkOut?.checkTime ??
      resolvedChecks?.checkOut?.checkTime;
    if (status === "No tuvo chequeo de salida" && checkOutTime) {
      return false;
    }

    const partnerId = Number(incident?.schedule?.partner?.id);
    const checkInId = Number(incident?.checkIn?.id);
    const dayKey = incident?.schedule?.exactDate
      ? moment.utc(incident.schedule.exactDate).format("YYYY-MM-DD")
      : "";
    if (partnerId && checkInId && status && dayKey) {
      const dedupeKey = `${partnerId}|${dayKey}|${status}|${checkInId}`;
      if (seenPartnerDay.has(dedupeKey)) {
        return false;
      }
      seenPartnerDay.add(dedupeKey);
    }
    return true;
  });
}

function appendIncidentDateKeys(incident: any, target: Set<string>) {
  const displayKey = incidentDisplayDateKeyDDMMYYYY(incident);
  if (displayKey) {
    target.add(displayKey);
  }
  const exactDate = incident?.schedule?.exactDate;
  if (exactDate) {
    target.add(moment.utc(exactDate).format("DD-MM-YYYY"));
  }
  const checkInDate =
    incident?.checkIn?.checkDate ??
    incident?.schedule?.assistanceCheck?.checkDate;
  if (checkInDate) {
    target.add(moment.utc(checkInDate).format("DD-MM-YYYY"));
  }
}

/** Fechas (DD-MM-YYYY) con al menos una incidencia visible en nómina. */
export function buildVisibleIncidentDateSet(incidents: any[]): Set<string> {
  const dates = new Set<string>();
  for (const incident of excludePendingLeaderReviewIncidents(incidents || [])) {
    appendIncidentDateKeys(incident, dates);
  }
  return dates;
}

/** Fechas con incidencia ya atendida por RH (liberación / acción final). */
export function buildResolvedPayrollIncidentDateSet(incidents: any[]): Set<string> {
  const dates = new Set<string>();
  for (const incident of incidents || []) {
    if (isPendingLeaderReviewIncident(incident)) {
      continue;
    }
    if (incident?.finalStatus === true || incident?.authorizeBy) {
      appendIncidentDateKeys(incident, dates);
    }
  }
  return dates;
}

/**
 * Punto naranja en gráfico / detalle de pago: incidencia visible que aún requiere
 * acción del líder (no las ocultas de “pendiente de revisión del líder”).
 */
export function buildLeaderPendingDotDateSet(incidents: any[]): Set<string> {
  const dates = new Set<string>();
  for (const incident of incidents || []) {
    if (isPendingLeaderReviewIncident(incident)) {
      continue;
    }
    if (incident?.finalStatus === true) {
      continue;
    }
    if (incident?.authorizeBy) {
      continue;
    }
    appendIncidentDateKeys(incident, dates);
  }
  return dates;
}

export function countVisiblePayrollIncidents(incidents: any[]): number {
  return (incidents || []).length;
}

/** Fechas con cualquier incidencia del periodo (incl. pendiente de revisión del líder). */
export function buildPayrollDisplayIncidentDateSet(incidents: any[]): Set<string> {
  const dates = new Set<string>();
  for (const incident of incidents || []) {
    appendIncidentDateKeys(incident, dates);
  }
  return dates;
}

/**
 * Días que aún requieren acción (líder o RH), incluyendo filas
 * «Pendiente de revisión del líder» y no programados.
 */
export function buildPayrollRhPendingDateSet(incidents: any[]): Set<string> {
  const dates = new Set<string>();
  for (const incident of incidents || []) {
    if (incident?.finalStatus === true) {
      continue;
    }
    if (incident?.authorizeBy) {
      continue;
    }
    appendIncidentDateKeys(incident, dates);
  }
  return dates;
}

function incidentInPayrollDateRange(
  incident: any,
  start: moment.Moment,
  end: moment.Moment
): boolean {
  const raw =
    incident?.schedule?.exactDate ||
    incident?.schedule?.assistanceCheck?.checkDate ||
    incident?.checkIn?.checkDate;
  if (!raw) {
    return false;
  }
  const day = moment.utc(raw).startOf("day");
  return day.isSameOrAfter(start, "day") && day.isSameOrBefore(end, "day");
}

/** Nómina: todas las incidencias del rango (sin ocultar pendientes del líder). */
export function filterIncidentsInPayrollDateRange(
  incidents: any[],
  startDate: string,
  endDate: string
): any[] {
  const start = moment.utc(startDate, "YYYY-MM-DD").startOf("day");
  const end = moment.utc(endDate, "YYYY-MM-DD").endOf("day");

  return (incidents || []).filter((incident) =>
    incidentInPayrollDateRange(incident, start, end)
  );
}
